import React from 'react';
import { Badge } from '../ui/badge';
import { GitMerge, Eye } from 'lucide-react';

// Panel de Informe de Vinculación (casos unificados por merge)
const InformeVinculacion = ({ caso, navigate }) => {
  const informe = caso.informe_vinculacion || {};
  const hijos = caso.casos_hijos || [];
  const conexiones = informe.conexiones || [];
  const contrapartes = informe.top_contrapartes || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <GitMerge size={20} className="text-violet-600" />
        <h3 className="text-lg font-bold text-slate-800">Informe de Vinculación</h3>
        <Badge className="bg-violet-100 text-violet-700">{hijos.length} casos unificados</Badge>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border rounded-lg p-3">
          <p className="text-[10px] text-slate-400 uppercase">Casos Unificados</p>
          <p className="text-xl font-bold text-violet-700">{informe.total_casos || hijos.length}</p>
        </div>
        <div className="bg-white border rounded-lg p-3">
          <p className="text-[10px] text-slate-400 uppercase">Conexiones</p>
          <p className="text-xl font-bold text-blue-600">{conexiones.length}</p>
        </div>
        <div className="bg-white border rounded-lg p-3">
          <p className="text-[10px] text-slate-400 uppercase">Score Máximo</p>
          <p className="text-xl font-bold" style={{ color: (informe.score_max || 0) >= 80 ? '#ef4444' : '#f59e0b' }}>{informe.score_max || caso.score_riesgo}</p>
        </div>
        <div className="bg-white border rounded-lg p-3">
          <p className="text-[10px] text-slate-400 uppercase">Contrapartes Clave</p>
          <p className="text-xl font-bold text-purple-600">{contrapartes.length}</p>
        </div>
      </div>

      {/* Conexiones */}
      {conexiones.length > 0 && (
        <div>
          <p className="text-sm font-semibold text-slate-700 mb-3">Conexiones Detectadas</p>
          <div className="space-y-2">
            {conexiones.map((cx, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100 text-xs">
                <span className="font-semibold text-slate-800 w-36 truncate">{cx.from_nombre}</span>
                <div className="flex-1 flex flex-wrap gap-1 justify-center">
                  {(cx.razones || []).map((r, ri) => (
                    <Badge key={ri} className={`text-[9px] ${r.tipo === 'Transferencia directa' ? 'bg-red-100 text-red-700 border border-red-200' : r.tipo === 'Contraparte compartida' ? 'bg-purple-100 text-purple-700 border border-purple-200' : r.tipo?.includes('Mismo') ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                      {r.tipo}{r.detalle ? `: ${r.detalle}` : ''}
                    </Badge>
                  ))}
                </div>
                <span className="font-semibold text-slate-800 w-36 truncate text-right">{cx.to_nombre}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Contrapartes clave */}
      {contrapartes.length > 0 && (
        <div>
          <p className="text-sm font-semibold text-purple-700 mb-3">Contrapartes Clave de la Red</p>
          <div className="overflow-x-auto rounded-lg border border-purple-200">
            <table className="w-full text-xs">
              <thead><tr className="bg-purple-50">
                <th className="p-2 text-left font-semibold text-purple-700">CVU</th>
                <th className="p-2 text-left font-semibold text-purple-700">Nombre</th>
                <th className="p-2 text-right font-semibold text-purple-700">Transacciones</th>
              </tr></thead>
              <tbody>
                {contrapartes.map((cp, i) => (
                  <tr key={i} className="border-t border-purple-100 hover:bg-purple-50/50">
                    <td className="p-2 font-mono text-[10px] text-slate-600">{cp.cvu}</td>
                    <td className="p-2 font-medium text-slate-800">{cp.nombre || <span className="italic text-slate-300">Sin identificar</span>}</td>
                    <td className="p-2 text-right font-bold text-purple-700">{cp.total_txs}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Casos hijos */}
      <div>
        <p className="text-sm font-semibold text-slate-700 mb-3">Casos Originales</p>
        <div className="space-y-1.5">
          {hijos.map((h, i) => (
            <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-100 hover:bg-slate-100 cursor-pointer text-xs"
              onClick={() => navigate(`/m/gestion-de-casos/caso/${h.id}`)}>
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-violet-400" />
                <span className="font-bold text-slate-800">{h.nombre || h.titulo || h.id.slice(0, 12)}</span>
                <Badge className="bg-slate-200 text-slate-500 text-[9px]">{h.estado_original}</Badge>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-bold" style={{ color: (h.score || 0) >= 80 ? '#ef4444' : '#f59e0b' }}>{h.score}</span>
                <span className="text-slate-400">{h.alertas} alertas</span>
                <Eye size={12} className="text-slate-400" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {informe.fecha_analisis && (
        <p className="text-[10px] text-slate-400 text-right">Análisis generado: {new Date(informe.fecha_analisis).toLocaleString('es-AR')}</p>
      )}
    </div>
  );
};

export default InformeVinculacion;
